import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { OrgAdminScreenProps } from "./OrgAdmin";
import { OrgAdminGeneralQuery } from "./__generated__/OrgAdminGeneralQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  AHText,
  AHIcon,
  TouchableBox,
  AHSeparator,
  Input,
  Card,
 Image, useUploadImage } from "arkh";
import { OrgButton } from "../OrgButton";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { OrgAdminGeneralSaveMutation } from "./__generated__/OrgAdminGeneralSaveMutation.graphql";
import { FormattedMessage } from "react-intl";
import { DropFileZone, droppedFileToImage } from "../../Files/DropFileZone";
import { OrgAdminGeneralUploadImageLinkMutation } from "./__generated__/OrgAdminGeneralUploadImageLinkMutation.graphql";
import { useOpenImagePicker } from "../../../components/mobile/useImageActionSheet";
import { Image_image$key } from "arkh/src/cross-platform/__generated__/Image_image.graphql";

export function OrgAdminGeneralScreen(
  props: OrgAdminScreenProps<"OrgAdminGeneral">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerOrgAdminGeneralScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgAdminGeneralScreen(
  props: OrgAdminScreenProps<"OrgAdminGeneral"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<OrgAdminGeneralQuery>(
    graphql`
      query OrgAdminGeneralQuery {
        organisation @required(action: THROW) {
          id
          name
          logo {
            id
            ...Image_image
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [generateUploadLink] =
    useMutation<OrgAdminGeneralUploadImageLinkMutation>(graphql`
      mutation OrgAdminGeneralUploadImageLinkMutation {
        organisation {
          uploadImageLink {
            url
            image {
              id
            }
          }
        }
      }
    `);

  const [save, isSaving] = useMutation<OrgAdminGeneralSaveMutation>(graphql`
    mutation OrgAdminGeneralSaveMutation($setName: String, $setLogo: String) {
      organisation {
        update(setName: $setName, setLogo: $setLogo) {
          id
          name
          logo {
            id
            ...Image_image
          }
        }
      }
    }
  `);

  const remoteLogoId = data.organisation.logo?.id ?? null;
  const remoteName = data.organisation.name;
  const [name, setName] = useState(remoteName);
  const [logo, setLogo] = useState<
    | { uri: string; id: string; type: "local" }
    | { id: string; image: Image_image$key & { id: string }; type: "remote" }
    | null
  >(
    data.organisation.logo
      ? {
          type: "remote",
          id: data.organisation.logo.id,
          image: data.organisation.logo,
        }
      : null
  );

  const isDirty = name !== remoteName || logo?.id !== remoteLogoId;
  useEffect(() => {
    setName(remoteName);
  }, [remoteName]);

  const intl_tokens = useIntlTokens();
  const { uploadImage, prepareForImageUpload } = useUploadImage();

  const onSetImage = useCallback(
    async (
      uri: string,
      { width, height }: { width: number; height: number }
    ) => {
      const uploadId = prepareForImageUpload({
        width,
        height,
        localImageURI: uri,
      });

      generateUploadLink({
        variables: {},
        onCompleted: (data) => {
          setLogo({
            type: "local",
            uri: uri,
            id: data.organisation.uploadImageLink.image.id,
          });
          uploadId.then((uploadId) => {
            uploadImage({
              uploadId,
              imageId: data.organisation.uploadImageLink.image.id,
              uploadURL: data.organisation.uploadImageLink.url,
            });
          });
        },
      });
    },
    [generateUploadLink, prepareForImageUpload, uploadImage]
  );

  const { openImagePicker } = useOpenImagePicker({
    onAddImage: onSetImage,
  });

  return (
    <DropFileZone
      type="IMAGE"
      deactivated={false}
      onDropFiles={async (files, nonApproved) => {
        files.map(async ({ file }) => {
          const { width, height, uri } = await droppedFileToImage(file);
          onSetImage(uri, { width, height });
        });
      }}
      maxSize={2000000}
    >
      <Box
        spaceHorizontal="medium"
        spaceTop="medium"
        color="surface-background-light"
        style={{ flex: 1 }}
      >
        <Box>
          <AHText spaceBottom="small" spaceHorizontal="medium">
            <FormattedMessage
              defaultMessage="Organisation Name"
              description="Org Admin General Settings Name Label"
            />
          </AHText>
          <Input value={name} onChangeText={(text) => setName(text)} />
        </Box>
        <Box spaceTop="large">
          <Box
            style={{ flexDirection: "row", alignItems: "center" }}
            spaceBottom="small"
          >
            <AHText spaceHorizontal="medium" style={{ flex: 1 }}>
              <FormattedMessage
                defaultMessage="Organisation Logo"
                description="Org Admin General Settings Logo Label"
              />
            </AHText>
            {logo ? (
              <TouchableBox
                onPress={() => {
                  openImagePicker();
                }}
                spaceHorizontal="medium"
              >
                <AHText color="primary" type="subtitle">
                  {intl_tokens.Change}
                </AHText>
              </TouchableBox>
            ) : null}
          </Box>
          <Card color="surface-primary" defaultBorder row>
            {logo ? (
              <Box
                style={{
                  height: 40,
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Image
                  {...logo}
                  contentPosition="left center"
                  contentFit="contain"
                  style={{
                    flexDirection: "row",
                    flex: 1,
                    height: "100%",
                  }}
                />
                <TouchableBox
                  onPress={() => {
                    setLogo(null);
                  }}
                  style={{
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  spaceLeft="medium"
                >
                  <AHIcon type="trash" color="negative" />
                </TouchableBox>
              </Box>
            ) : (
              <OrgButton
                tintColor="text-inverted"
                backgroundColor="primary"
                title={intl_tokens.Upload}
                onPress={openImagePicker}
              />
            )}
          </Card>
        </Box>
      </Box>
      <AHSeparator />
      <Box
        color="surface"
        space="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {isDirty ? (
          <OrgButton
            title={intl_tokens.Save}
            backgroundColor="primary"
            tintColor="text-inverted"
            loading={isSaving}
            onPress={() => {
              save({
                variables: {
                  setName: name !== remoteName ? name : undefined,
                  setLogo:
                    logo?.id !== remoteLogoId ? (logo?.id ?? null) : undefined,
                },
                onCompleted: (data) => {
                  props.navigation.goBack();
                },
              });
            }}
          />
        ) : null}
      </Box>
    </DropFileZone>
  );
}
